import React, { memo, useMemo, useRef } from "react";
import { Container } from "@pixi/react";
import { Settings, GAME_LAYOUT } from "../../../settings";
import OverlayReel from "../OverlayReel/OverlayReel";
import OverlaySymbol from "../OverlaySymbol/OverlaySymbol";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useSelector } from "react-redux";
import { selectHoldAndWinActive, selectFreeSpinActive } from "../../../State/selectors";
import { selectFreeSpinsActive } from "../../../State/Slices/freeSpinsSlice";

/**
 * Flat overlay container for the base game, in native units.
 *
 * All 15 symbols (5 reels × 3 rows) are siblings in ONE sortable container, so
 * a symbol on one reel can render above one on another (PIXI sorts only among
 * siblings).
 *
 * OverlayReel still exists per reel: it owns that reel's OverlayReelController
 * and hosts the full-reel expanding wild spine. It receives the symbols as a
 * ref slice; it does not parent them.
 */
const OverlayReelSet = memo(({ symbolWidth, symbolHeight, reelSpacing }) => {
  const { overlayReelRefs } = useReelRefs();
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  // This game's bonus flag (see ReelSet.jsx). `freeSpinActive` is the legacy
  // flag and is never raised here.
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const symbolRefs = useRef(
    Array.from(
      { length: GAME_LAYOUT.REEL_COUNT },
      () => new Array(GAME_LAYOUT.SYMBOLS_VISIBLE),
    ),
  );

  // Memoize container Y position calculation
  const containerY = useMemo(
    () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
    [symbolHeight]
  );

  // ── `visible`, not alpha ──
  //
  // alpha 0.001 still renders: PIXI skips a subtree only when visible or
  // renderable is false, or worldAlpha <= 0. This is the heaviest layer (one
  // pooled spine per symbol per cell), so it must not draw through the bonus.
  //
  // Alpha is for keeping a subtree warm (PIXI's texture GC unloads textures
  // left undrawn for 60s). Not needed here: the bonus room draws the same
  // symbol spines, from the same atlas pages, for the whole round.
  const containerVisible = useMemo(
    () => !holdAndWinActive && !freeSpinActive && !freeSpinsActive,
    [holdAndWinActive, freeSpinActive, freeSpinsActive]
  );

  // All 15 cell positions; each symbol carries its own x.
  const symbolPositions = useMemo(() => {
    const positions = [];
    const centerOffset = (GAME_LAYOUT.REEL_COUNT - 1) / 2;
    for (let ri = 0; ri < GAME_LAYOUT.REEL_COUNT; ri++) {
      for (let si = 0; si < GAME_LAYOUT.SYMBOLS_VISIBLE; si++) {
        positions.push({
          reelIndex: ri,
          symbolIndex: si,
          x: reelSpacing * (ri - centerOffset),
          y: symbolHeight * si,
        });
      }
    }
    return positions;
  }, [reelSpacing, symbolHeight]);

  return (
    <Container
      name="spine_overlay"
      zIndex={5}
      y={containerY}
      visible={containerVisible}
      sortableChildren
    >
      {/* The 15 sortable cells. Emitted before the reels so their refs are
          attached by the time a reel's effect builds its controller. */}
      {symbolPositions.map(({ reelIndex, symbolIndex, x, y }) => (
        <OverlaySymbol
          key={`${reelIndex}_${symbolIndex}`}
          index={symbolIndex}
          reelIndex={reelIndex}
          x={x}
          y={y}
          ref={(el) => {
            symbolRefs.current[reelIndex][symbolIndex] = el;
          }}
        />
      ))}

      {/* Controller hosts — one per reel, carrying only the expanding wild. */}
      {GAME_LAYOUT.REEL_INDICES.map((index) => (
        <OverlayReel
          key={index}
          symbolHeight={symbolHeight}
          symbolWidth={symbolWidth}
          reelSpacing={reelSpacing}
          symbolRefs={symbolRefs.current[index]}
          ref={(el) => {
            overlayReelRefs.current[index] = el;
          }}
          index={index}
        />
      ))}
    </Container>
  );
});

export default OverlayReelSet;
